import React, { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import Select from 'react-select';
import { useDeviceInfo } from '../../DeviceInfo';
import { useOccurrenceData } from './hooks/useOccurrenceData';
import { groupOccurrences, deriveFilterOptions } from './utils/occurrenceHelpers';
import OccurrenceFilters from './components/OccurrenceFilters';
import DetailTable from './components/DetailTable';
import OccurrenceCharts from './components/OccurrenceCharts';
import OccurrenceSummaryTab from './components/OccurrenceSummaryTab';
import { useOccurrenceSelection } from './hooks/useOccurrenceSelection';
import { siteSelectStyles } from './utils/selectStyles';
import './occurrences.scss';

const INITIAL_FILTERS = {
    site: null,
    department: null,
    staff: null,
    role: null,
    form: null,
    dateFrom: '',
    dateTo: '',
    search: '',
};

const Occurrences = forwardRef(({
    preloadedData,
    setPreloadedData,
    dataLoaded,
    setDataLoaded,
    activeSubTab,
    onSubTabChange,
    hasAdvancedOccurrences,
    onSummaryEnabledChange,
}, ref) => {
    const { deviceInfo } = useDeviceInfo();
    const isMobileView = isMobile || isTablet;

    const {
        occurrenceData,
        loading,
        error,
        dataFetched,
        refresh,
    } = useOccurrenceData(deviceInfo, preloadedData);

    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [overviewSite, setOverviewSite] = useState(null);
    const [overviewDateFrom, setOverviewDateFrom] = useState('');
    const [overviewDateTo, setOverviewDateTo] = useState('');

    const {
        selectedOccurrence,
        isFollowUp,
        isSummaryEnabled,
        handleViewOccurrence,
        handleClearSelection,
    } = useOccurrenceSelection({ onTabChange: onSubTabChange });

    // Group flat rows into incidents 
    const incidents = useMemo(() => groupOccurrences(occurrenceData), [occurrenceData]);

    // Derive filter options from all incidents
    const filterOptions = useMemo(() => deriveFilterOptions(incidents), [incidents]);

    // Apply filters
    const filteredIncidents = useMemo(() => {
        let result = incidents;

        if (filters.site) result = result.filter(i => i.siteName === filters.site.value);
        if (filters.department) result = result.filter(i => i.department === filters.department.value);
        if (filters.staff) result = result.filter(i => i.staffName === filters.staff.value);
        if (filters.role) result = result.filter(i => i.roleName === filters.role.value);
        if (filters.form) result = result.filter(i => i.formTitle === filters.form.value);

        if (filters.dateFrom) {
            const from = new Date(filters.dateFrom);
            result = result.filter(i => i.dateOfIncident && new Date(i.dateOfIncident) >= from);
        }
        if (filters.dateTo) {
            const to = new Date(filters.dateTo);
            to.setHours(23, 59, 59, 999);
            result = result.filter(i => i.dateOfIncident && new Date(i.dateOfIncident) <= to);
        }
        if (filters.search) {
            const term = filters.search.toLowerCase();
            result = result.filter(i =>
                (i.staffName && i.staffName.toLowerCase().includes(term)) ||
                (i.siteName && i.siteName.toLowerCase().includes(term)) ||
                (i.notes && i.notes.toLowerCase().includes(term)) ||
                (i.formTitle && i.formTitle.toLowerCase().includes(term))
            );
        }

        return result;
    }, [incidents, filters]);

    const filteredOverviewIncidents = useMemo(() => {
        let result = incidents;
        if (overviewSite) result = result.filter(i => i.siteName === overviewSite.value);
        if (overviewDateFrom) {
            const from = new Date(overviewDateFrom);
            result = result.filter(i => i.dateOfIncident && new Date(i.dateOfIncident) >= from);
        }
        if (overviewDateTo) {
            const to = new Date(overviewDateTo);
            to.setHours(23, 59, 59, 999);
            result = result.filter(i => i.dateOfIncident && new Date(i.dateOfIncident) <= to);
        }
        return result;
    }, [incidents, overviewSite, overviewDateFrom, overviewDateTo]);

    const handleFilterChange = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
    }, []);

    // Clear selection when navigating to detail tab
    useEffect(() => {
        if (activeSubTab === 'detail') {
            handleClearSelection();
        }
    }, [activeSubTab, handleClearSelection]);

    // Notify parent of summary enabled state changes
    useEffect(() => {
        onSummaryEnabledChange?.(isSummaryEnabled);
    }, [isSummaryEnabled, onSummaryEnabledChange]);

    // Sync data back to parent
    useEffect(() => {
        if (dataFetched && !dataLoaded && occurrenceData.length > 0 && setPreloadedData && setDataLoaded) {
            setPreloadedData({ occurrenceData });
            setDataLoaded(true);
        }
    }, [dataFetched, dataLoaded, occurrenceData, setPreloadedData, setDataLoaded]);

    const summaryTabLabel = hasAdvancedOccurrences ? 'Follow-Up' : 'View';

    // Expose renderFilters for mobile sidebar + isSummaryEnabled
    useImperativeHandle(ref, () => ({
        isSummaryEnabled,
        renderFilters: () => (
            <div className="sidebar-filter-section">
                <div className="sidebar-filter-item">
                    <label>Site</label>
                    <Select
                        value={filters.site}
                        onChange={(val) => handleFilterChange('site', val)}
                        options={filterOptions.sites}
                        placeholder="All Sites"
                        isClearable
                        isSearchable
                        styles={siteSelectStyles}
                    />
                </div>
                <div className="sidebar-filter-item">
                    <label>Department</label>
                    <Select
                        value={filters.department}
                        onChange={(val) => handleFilterChange('department', val)}
                        options={filterOptions.departments}
                        placeholder="All Departments"
                        isClearable
                        isSearchable
                        styles={siteSelectStyles}
                    />
                </div>
                <div className="sidebar-filter-item">
                    <label>Form</label>
                    <Select
                        value={filters.form}
                        onChange={(val) => handleFilterChange('form', val)}
                        options={filterOptions.forms}
                        placeholder="All Forms"
                        isClearable
                        isSearchable
                        styles={siteSelectStyles}
                    />
                </div>
            </div>
        ),
    }), [filters, filterOptions, handleFilterChange, isSummaryEnabled]);

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="small-spinner">
                    <span></span><span></span><span></span><span></span>
                </div>
                <p className="mt-3" style={{ color: 'white' }}>Loading occurrence data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>Error loading data: {error}</p>
                <button className="occurrence__retry-btn" onClick={refresh}>Retry</button>
            </div>
        );
    }

    if (!occurrenceData || occurrenceData.length === 0) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>No occurrence data available.</p>
            </div>
        );
    }

    return (
        <div className="occurrences">
            {!isMobileView && (
                <div className="table-level-tabs mb-2 d-flex align-items-center">
                    <button
                        className={`table-tab ${activeSubTab === 'overview' ? 'active' : ''}`}
                        onClick={() => onSubTabChange('overview')}
                    >
                        Overview
                    </button>
                    <button
                        className={`table-tab ${activeSubTab === 'detail' ? 'active' : ''}`}
                        onClick={() => onSubTabChange('detail')}
                    >
                        Detail
                    </button>
                    <button
                        className={`table-tab ${activeSubTab === 'summary' ? 'active' : ''} ${!isSummaryEnabled ? 'table-tab--disabled' : ''}`}
                        onClick={() => isSummaryEnabled && onSubTabChange('summary')}
                    >
                        {summaryTabLabel}
                    </button>
                </div>
            )}

            {activeSubTab === 'overview' && (
                <div className="occurrences__panel">
                    <div className="occurrences__overview-filters">
                        <Select
                            value={overviewSite}
                            onChange={setOverviewSite}
                            options={filterOptions.sites}
                            placeholder="All Sites"
                            isClearable
                            isSearchable
                            classNamePrefix="react-select"
                            styles={siteSelectStyles}
                        />
                        <input
                            type="date"
                            className="occurrences__date-input"
                            value={overviewDateFrom}
                            onChange={(e) => setOverviewDateFrom(e.target.value)}
                        />
                        <span className="occurrences__date-separator">to</span>
                        <input
                            type="date"
                            className="occurrences__date-input"
                            value={overviewDateTo}
                            onChange={(e) => setOverviewDateTo(e.target.value)}
                        />
                    </div>
                    <OccurrenceCharts incidents={filteredOverviewIncidents} />
                </div>
            )}

            {activeSubTab === 'detail' && (
                <div className="occurrences__panel">
                    {!isMobileView && (
                        <OccurrenceFilters
                            filterOptions={filterOptions}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={handleClearFilters}
                        />
                    )}
                    <DetailTable
                        incidents={filteredIncidents}
                        hasAdvancedOccurrences={hasAdvancedOccurrences}
                        onViewIncident={(inc) => handleViewOccurrence(inc, !!hasAdvancedOccurrences)}
                    />
                </div>
            )}

            {activeSubTab === 'summary' && selectedOccurrence && (
                <div className="occurrences__panel">
                    <OccurrenceSummaryTab
                        occurrence={selectedOccurrence}
                        isFollowUp={isFollowUp}
                        hasAdvancedOccurrences={hasAdvancedOccurrences}
                        allIncidents={incidents}
                        onBack={() => onSubTabChange('detail')}
                        onFollowUpCreated={() => {
                            refresh();
                            onSubTabChange('detail');
                        }}
                    />
                </div>
            )}
        </div>
    );
});

export default Occurrences;
